import type { FC, ReactNode } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';

import { sanitizeInputLoadablesForSSR } from './sanitizeInputLoadablesForSSR.ts';

type FullCycleSSR = {
	active: boolean;
	htmlOutput: Record<string, string>;
};

export const FULLCYCLE_MEASURE_PREFIX = 'ssr-app/render/render-full-cycle';

declare global {
	interface Window {
		__FULLCYCLE_SSR__?: FullCycleSSR;
	}
}

function createFullCycleRenderRegistry() {
	const registry = new Set<Function>();

	function register(callback: Function): void {
		registry.add(callback);
	}

	function unregister(callback: Function): void {
		registry.delete(callback);
	}

	function notifyRenderComplete(): void {
		registry.forEach((callback) => callback());
	}

	return { register, unregister, notifyRenderComplete };
}

// This registry is used to keep track of all the components that need to be rendered in SSR during a full cycle render
export const fullCycleRenderRegistry: {
	register: (callback: Function) => void;
	unregister: (callback: Function) => void;
	notifyRenderComplete: () => void;
} = createFullCycleRenderRegistry();

// This component is used to facilite full cycle rendering in SSR for nested components.
// The intended usage is:
// - Wrap the component you want to render in SSR with this component
// - Pass a unique name prop to this component
// - The children of this component will be rendered in SSR during the full cycle render, and the output will be stored
// - Subsequent renders of the component during normal (non full-cycle) rendering will render the previously stored output
export const FullCycleRender: FC<{
	name: string;
	children: ReactNode;
}> = ({ name, children }) => {
	const fullCycleSSRState = window.__FULLCYCLE_SSR__;
	const containerRef = useRef<HTMLDivElement>(null);

	const onFullCycleRenderComplete = useCallback(() => {
		if (containerRef.current && fullCycleSSRState) {
			const html = sanitizeInputLoadablesForSSR(containerRef.current.innerHTML);
			fullCycleSSRState.htmlOutput[name] = html;
		}
	}, [name, fullCycleSSRState]);

	useEffect(() => {
		fullCycleRenderRegistry.register(onFullCycleRenderComplete);

		return () => fullCycleRenderRegistry.unregister(onFullCycleRenderComplete);
	}, [onFullCycleRenderComplete]);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR || !fullCycleSSRState) {
		return null;
	}

	if (fullCycleSSRState.active) {
		return <div ref={containerRef}>{children}</div>;
	} else {
		const html = fullCycleSSRState.htmlOutput[name];

		return (
			// eslint-disable-next-line react/no-danger
			<div data-test-id="fullCycleOutput" dangerouslySetInnerHTML={{ __html: html }} />
		);
	}
};

// This component is required to ensure hydration of the DOM subtree that is rendered
// via FullCycleRender, as the wrapper is necessary on the server side.
export const FullCycleRenderWrapper: FC<{
	children: ReactNode;
}> = ({ children }) => {
	return <div data-test-id="fullCycleOutput">{children}</div>;
};
